// App-wide navigation hooks, built on TanStack Router.
//
// Components use these instead of importing the router directly, so most of them don't depend on
// route types: `usePathname()`, `useSearchParams()` (a read-only URLSearchParams) and `useAppRouter()`
// (`push`, `replace`, `refresh`, `back`, `forward`, `prefetch`). `refresh()` reruns the current
// route's loaders (`router.invalidate()`), which is how server-loaded data (auth state, the portal
// view) is reloaded after a mutation.
//
// Route modules can use TanStack Router's own APIs (`Route.useSearch()`, `redirect`, `notFound`)
// directly; they own their typed params and search schemas.
import { type AnyRouter, useLocation, useRouter, useRouterState } from "@tanstack/react-router";
import { useMemo } from "react";
import { resolveCurrentScopePath } from "./PortalPaths";

export interface AppRouter {
  push: (href: string, options?: { scroll?: boolean }) => Promise<void>;
  replace: (href: string, options?: { scroll?: boolean }) => Promise<void>;
  /** Reloads the data of every active route (server loaders included). */
  refresh: () => Promise<void>;
  back: () => void;
  forward: () => void;
  prefetch: (href: string) => Promise<void>;
}

/** The organisation the portal has open (its layout data), if the router is on a portal page. */
export const openOrganisationId = (router: Pick<AnyRouter, "state"> | undefined) =>
  (
    router?.state?.matches?.find((match) => match.routeId === "/_authed/portal")?.loaderData as
      | { selectedOrgId?: string | null }
      | undefined
  )?.selectedOrgId ?? undefined;

// Links to the current project's or organisation's pages resolve against the page's scope.
const resolve = (router: AnyRouter, href: string) =>
  resolveCurrentScopePath(href, router.state.location.pathname, openOrganisationId(router));

const createAppRouter = (router: AnyRouter): AppRouter => ({
  push: (href, options) =>
    router.navigate({ href: resolve(router, href), resetScroll: options?.scroll ?? true }),
  replace: (href, options) =>
    router.navigate({
      href: resolve(router, href),
      replace: true,
      resetScroll: options?.scroll ?? true,
    }),
  refresh: () => router.invalidate({ sync: true }),
  back: () => router.history.back(),
  forward: () => router.history.forward(),
  prefetch: async (href) => {
    await router.preloadRoute({ href } as never).catch(() => undefined);
  },
});

let activeRouter: AnyRouter | undefined;

/** Called once by src/router.tsx in the browser, so non-React code can navigate. */
export const setActiveRouter = (router: AnyRouter) => {
  if (typeof window !== "undefined") activeRouter = router;
};

/** The browser's router, for code outside React (undefined on the server). */
export const getActiveRouter = (): AppRouter | undefined =>
  activeRouter ? createAppRouter(activeRouter) : undefined;

/**
 * Replaces the URL's query string without adding a history entry or scrolling, keeping router
 * state in sync (unlike a bare `history.replaceState`).
 */
export const replaceSearch = (search: string) => {
  const query = search.replace(/^\?/, "");
  const href = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  if (activeRouter) {
    void activeRouter.navigate({ href, replace: true, resetScroll: false });
  } else {
    window.history.replaceState(window.history.state, "", href);
  }
};

export const useAppRouter = (): AppRouter => {
  const router = useRouter();
  return useMemo(() => createAppRouter(router), [router]);
};

export const usePathname = (): string => useLocation({ select: (location) => location.pathname });

/**
 * The path and query of the page currently on screen. Unlike `usePathname`, which changes as soon
 * as a navigation starts, this changes once the new route's loaders resolve and it renders.
 */
export const useResolvedHref = (): string =>
  useRouterState({
    select: (state) => {
      const location = state.resolvedLocation ?? state.location;
      return `${location.pathname}${location.searchStr}`;
    },
  });

/** The current query string as read-only URLSearchParams (stable until the query changes). */
export const useSearchParams = (): URLSearchParams => {
  const searchStr = useLocation({ select: (location) => location.searchStr });
  return useMemo(() => new URLSearchParams(searchStr), [searchStr]);
};
